import { ApiError, type Api } from './api';
import { describePoint, searchLandmarks, LANDMARKS, type Landmark } from './places';
import { haversine } from './geo';
import type { ApiPlace, LatLng, Place } from './types';

/**
 * Place search with Ola Maps through the API (docs/RIDER-WEB-TODO.md §1), and the built-in landmark list as the
 * quiet fallback: any failure (503 PLACES_UNAVAILABLE, no network, 429) shows landmark matches without an error,
 * and after a failure Ola isn't asked again for a minute so each key press doesn't wait on a failing call.
 */
const RETRY_MS = 60_000;
let downUntil = 0;

export const placesUp = (now = Date.now()) => now >= downUntil;
/** For tests. */
export const resetPlaces = () => {
  downUntil = 0;
};
const unavailable = (e: unknown) => e instanceof ApiError && (e.code === 'PLACES_UNAVAILABLE' || e.status === 0 || e.status === 429 || e.status >= 500);

/** One search result row: Ola's place or a landmark, the same shape for the list. */
export type PlaceRow = { id: string; name: string; sub: string; place: Place; highlight: number; source: 'ola' | 'landmark' };

export const landmarkRow = (l: Landmark, highlight = 0): PlaceRow => ({
  id: `lm:${l.id}`,
  name: l.name,
  sub: l.area,
  place: { lat: l.lat, lng: l.lng, address: `${l.name}, ${l.area}` },
  highlight,
  source: 'landmark',
});

export const olaRow = (p: ApiPlace, q: string): PlaceRow => ({
  id: `ola:${p.id || `${p.lat},${p.lng}`}`,
  name: p.name,
  sub: p.address,
  place: { lat: p.lat, lng: p.lng, address: p.address ? `${p.name}, ${p.address}` : p.name },
  highlight: p.name.toLowerCase().startsWith(q.trim().toLowerCase()) ? q.trim().length : 0,
  source: 'ola',
});

/** The nearest landmark destinations (empty query: no API call). */
export const nearbyRows = (from: LatLng, n = 6): PlaceRow[] =>
  LANDMARKS.filter((l) => l.kind === 'place')
    .sort((a, b) => haversine(from, a) - haversine(from, b))
    .slice(0, n)
    .map((l) => landmarkRow(l));

export const landmarkRows = (q: string): PlaceRow[] => searchLandmarks(q).map((m) => landmarkRow(m.landmark, m.highlight));

/**
 * Rows for a typed query: Ola's suggestions when search is up, landmark matches when Ola finds nothing or fails.
 * Throws only for a caller that was cancelled (never for a provider failure).
 */
export async function searchPlaces(api: Api, q: string, near: LatLng | null): Promise<PlaceRow[]> {
  const query = q.trim();
  if (query.length < 2 || !placesUp()) return landmarkRows(query);
  try {
    const { items } = await api.placesAutocomplete(query, near);
    return items.length ? items.map((p) => olaRow(p, query)) : landmarkRows(query);
  } catch (e) {
    if (unavailable(e)) downUntil = Date.now() + RETRY_MS;
    return landmarkRows(query);
  }
}

/** What a point is called: Ola's address when it has one, else the nearest landmark ("Main Bazaar, near SBI ATM"). */
export type PointName = { title: string; sub: string | null; outside: boolean; source: 'ola' | 'landmark' };

export const landmarkName = (at: LatLng): PointName => ({ title: describePoint(at).title, sub: null, outside: false, source: 'landmark' });

export async function namePoint(api: Api, at: LatLng): Promise<PointName> {
  if (!placesUp()) return landmarkName(at);
  try {
    const p = await api.reversePlace(at);
    if (!p.name && !p.address) return landmarkName(at);
    return { title: p.name || p.address, sub: p.name && p.address ? p.address : null, outside: false, source: 'ola' };
  } catch (e) {
    if (e instanceof ApiError && e.code === 'OUTSIDE_SERVICE_AREA') return { ...landmarkName(at), outside: true };
    if (unavailable(e)) downUntil = Date.now() + RETRY_MS;
    return landmarkName(at);
  }
}

/** The address text sent with a booking / saved on a place. */
export const addressOf = (n: PointName) => (n.sub ? `${n.title}, ${n.sub}` : n.title);
